import { DoorsConfig } from 'tower/types';
import {
  Direction,
  gridMove,
  oppositeDirection,
  randomRange,
  DIRECTIONS,
  weightedIndex,
  getOrientation,
} from 'tower/util';
import BattleScene from '.';

export class MazeNode {
  public links: { [key in Direction]: null | boolean } = {
    [Direction.Up]: false,
    [Direction.Down]: false,
    [Direction.Left]: false,
    [Direction.Right]: false,
  };

  constructor(
    public maze: Maze,
    public x: number,
    public y: number,
  ) {}

  get isEdge() {
    return this.x === 0 || this.x === this.maze.width - 1 || this.y === 0 || this.y === this.maze.height - 1;
  }

  get isCorner() {
    return (
      (this.x === 0 && this.y === 0) ||
      (this.x === 0 && this.y === this.maze.height - 1) ||
      (this.x === this.maze.width - 1 && this.y === 0) ||
      (this.x === this.maze.width - 1 && this.y === this.maze.height - 1)
    );
  }

  get openDirections(): Direction[] {
    const directions = [];
    for (const [direction, link] of Object.entries(this.links)) {
      if (link === false) {
        directions.push(direction);
      }
    }
    return directions as Direction[];
  }

  /** Check if the wall in a given direction is on the edge of the maze. */
  isEdgeWall(direction: Direction) {
    return (
      this.isCorner ||
      (this.x === 0 && direction !== Direction.Right) ||
      (this.x === this.maze.width - 1 && direction !== Direction.Left) ||
      (this.y === 0 && direction !== Direction.Down) ||
      (this.y === this.maze.height - 1 && direction !== Direction.Up)
    );
  }

  setLinks(up: boolean | null, right: boolean | null, down: boolean | null, left: boolean | null) {
    this.links.up = up;
    this.links.right = right;
    this.links.down = down;
    this.links.left = left;
  }

  setLink(direction: Direction, value: boolean | null) {
    this.links[direction] = value;
  }
}

export default class Maze {
  public nodes: MazeNode[];
  tilemap!: Phaser.Tilemaps.Tilemap;
  collisionTilemap!: Phaser.Tilemaps.Tilemap;
  tileset!: Phaser.Tilemaps.Tileset;
  collisionTileset!: Phaser.Tilemaps.Tileset;
  layer!: Phaser.Tilemaps.TilemapLayer;
  collisionLayer!: Phaser.Tilemaps.TilemapLayer;
  collider!: Phaser.Physics.Arcade.Collider;
  private nullNode: MazeNode;

  constructor(
    public scene: BattleScene,
    public x: number,
    public y: number,
    public width: number,
    public height: number,
    public doors: DoorsConfig,
  ) {
    this.nodes = [];
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        this.nodes.push(new MazeNode(this, x, y));
      }
    }

    this.clear();

    this.nullNode = new MazeNode(this, -1, -1);
  }

  create(serializedLinks?: string) {
    const { scene, width, height, x, y } = this;
    this.tilemap = scene.make.tilemap({ tileWidth: 8, tileHeight: 8, width: width * 3 - 2, height: height * 3 - 2 });
    this.collisionTilemap = scene.make.tilemap({
      tileWidth: 4,
      tileHeight: 4,
      width: width * 6 - 1,
      height: height * 6 - 1,
    });

    this.tileset = this.tilemap.addTilesetImage('maze', 'maze', 8, 8, 0, 0)!;
    this.collisionTileset = this.collisionTilemap.addTilesetImage('collisionTiles', 'collisionTiles', 4, 4, 0, 0)!;

    this.layer = this.tilemap.createBlankLayer('maze', this.tileset, x, y)!;
    this.collisionLayer = this.collisionTilemap
      .createBlankLayer('collisionMaze', this.collisionTileset, x - 2, y - 2)!
      .setDepth(1000);

    this.tilemap.setCollisionBetween(0, 15, true);
    this.tilemap.setCollisionBetween(17, 32, true);

    this.collisionTilemap.setCollisionBetween(0, 15, true);
    this.collisionTilemap.setCollisionBetween(17, 32, true);

    // this.collisionTilemap.putTilesAt(
    //   [
    //     [
    //       -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1,
    //       0,
    //     ],
    //     [
    //       0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0,
    //       -1, 0,
    //     ],
    //     [
    //       -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1,
    //       0,
    //     ],
    //     [
    //       0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0,
    //       -1, 0,
    //     ],
    //     [
    //       -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1,
    //       0,
    //     ],
    //     [
    //       0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0,
    //       -1, 0,
    //     ],
    //     [
    //       -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1,
    //       0,
    //     ],
    //     [
    //       0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0,
    //       -1, 0,
    //     ],
    //     [
    //       -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1,
    //       0,
    //     ],
    //     [
    //       0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0,
    //       -1, 0,
    //     ],
    //     [
    //       -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1,
    //       0,
    //     ],
    //     [
    //       0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0, -1, 0,
    //       -1, 0,
    //     ],
    //   ],
    //   0,
    //   0,
    // );

    this.collider = scene.physics.add.collider(scene.player, this.collisionLayer);

    if (serializedLinks) {
      this.deserializeLinks(serializedLinks);
      this.syncTilemap();
    }
  }

  destroy() {
    this.tilemap.destroy();
    this.layer.destroy();
    this.collider.destroy();
  }

  get edges() {
    return this.nodes.filter((n) => n.isEdge);
  }

  putTileAt(tileIndex: number, tileX: number, tileY: number) {
    this.tilemap.putTileAt(tileIndex, tileX, tileY);

    // Fill in collision tilemap
    const cx = tileX * 2;
    const cy = tileY * 2;
    switch (tileIndex) {
      case 1:
        this.collisionTilemap.putTilesAt(
          [
            [-1, 0, -1],
            [-1, 0, -1],
            [-1, 0, -1],
          ],
          cx,
          cy,
        );
        return;
      case 2:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [0, 0, 0],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      case 17:
        this.collisionTilemap.putTilesAt(
          [
            [-1, 0, -1],
            [-1, 0, -1],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      case 18:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [-1, 0, 0],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      case 19:
        this.collisionTilemap.putTilesAt(
          [
            [-1, 0, -1],
            [-1, 0, 0],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      case 20:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [-1, 0, -1],
            [-1, 0, -1],
          ],
          cx,
          cy,
        );
        return;
      case 21:
        this.collisionTilemap.putTilesAt(
          [
            [-1, 0, -1],
            [-1, 0, -1],
            [-1, 0, -1],
          ],
          cx,
          cy,
        );
        return;
      case 22:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [-1, 0, 0],
            [-1, 0, -1],
          ],
          cx,
          cy,
        );
        return;
      case 23:
        this.collisionTilemap.putTilesAt(
          [
            [-1, 0, -1],
            [-1, 0, 0],
            [-1, 0, -1],
          ],
          cx,
          cy,
        );
        return;
      case 24:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [0, 0, -1],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      case 25:
        this.collisionTilemap.putTilesAt(
          [
            [-1, 0, -1],
            [0, 0, -1],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      case 26:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [0, 0, 0],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      case 27:
        this.collisionTilemap.putTilesAt(
          [
            [-1, 0, -1],
            [0, 0, 0],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      case 28:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [0, 0, -1],
            [-1, 0, -1],
          ],
          cx,
          cy,
        );
        return;
      case 29:
        this.collisionTilemap.putTilesAt(
          [
            [-1, 0, -1],
            [0, 0, -1],
            [-1, 0, -1],
          ],
          cx,
          cy,
        );
        return;
      case 30:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [0, 0, 0],
            [-1, 0, -1],
          ],
          cx,
          cy,
        );
        return;
      case 31:
        this.collisionTilemap.putTilesAt(
          [
            [-1, 0, -1],
            [0, 0, 0],
            [-1, 0, -1],
          ],
          cx,
          cy,
        );
        return;
      case 0:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [-1, 0, -1],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      case -1:
      case 16:
        this.collisionTilemap.putTilesAt(
          [
            [-1, -1, -1],
            [-1, -1, -1],
            [-1, -1, -1],
          ],
          cx,
          cy,
        );
        return;
      default:
        console.log(tileIndex);
        return;
    }
  }

  getNode(x: number, y: number) {
    return this.nodes[y * this.width + x] ?? this.nullNode;
  }

  /** Return nodes at a given node space, in the order [top left, top right, bottom left, bottom right]. */
  getNodesAtSpace(x: number, y: number) {
    return [this.getNode(x, y), this.getNode(x + 1, y), this.getNode(x, y + 1), this.getNode(x + 1, y + 1)];
  }

  worldXYToSpaceXY(wx: number, wy: number): [number, number] {
    const topLeftNodeX = this.layer.x + 4;
    const topLeftNodeY = this.layer.y + 4;
    return [Math.floor((wx - topLeftNodeX) / 24), Math.floor((wy - topLeftNodeY) / 24)];
  }

  spaceXYToWorldXY(sx: number, sy: number): [number, number] {
    const topLeftSpaceX = this.layer.x + 16;
    const topLeftSpaceY = this.layer.y + 16;
    return [topLeftSpaceX + sx * 24, topLeftSpaceY + sy * 24];
  }

  /** Return nodes at a given world position, in the order [top left, top right, bottom left, bottom right]. */
  getNodesAtWorldXY(wx: number, wy: number) {
    return this.getNodesAtSpace(...this.worldXYToSpaceXY(wx, wy));
  }

  /** Return surrounding walls for the space at the given world XY. */
  getWallsAtWorldXY(wx: number, wy: number) {
    const [topLeftNode, _topRightNode, _bottomLeftNode, bottomRightNode] = this.getNodesAtSpace(
      ...this.worldXYToSpaceXY(wx, wy),
    );
    return {
      [Direction.Up]: topLeftNode.links.right,
      [Direction.Right]: bottomRightNode.links.up,
      [Direction.Down]: bottomRightNode.links.left,
      [Direction.Left]: topLeftNode.links.down,
    };
  }

  getOpenDirectionsAtWorldXY(wx: number, wy: number) {
    const walls = this.getWallsAtWorldXY(wx, wy);
    return Object.entries(walls)
      .filter(([_direction, blocked]) => blocked === false)
      .map(([direction, _blocked]) => direction as Direction);
  }

  getLinkAtWorldXY(wx: number, wy: number): [MazeNode, Direction] | [null, null] {
    // No tiles outside the layer
    if (
      wx < this.layer.x ||
      wx >= this.layer.x + this.layer.width ||
      wy < this.layer.y ||
      wy >= this.layer.y + this.layer.height
    ) {
      return [null, null];
    }

    const tile = this.layer.getTileAtWorldXY(wx, wy, true);

    // Exclude tiles that aren't a node or a wall (e.g. divisible by 3)
    if (!(tile.x % 3 === 0 || tile.y % 3 === 0)) {
      return [null, null];
    }

    // Exclude nodes, which are divisible by 3 in both dimensions
    if (tile.x % 3 === 0 && tile.y % 3 === 0) {
      return [null, null];
    }

    // Exclude the outer walls
    if (tile.x === 0 || tile.x === this.width * 3 - 2 || tile.y === 0 || tile.y === this.height * 3 - 2) {
      return [null, null];
    }

    const node = this.getNode(Math.floor(tile.x / 3), Math.floor(tile.y / 3));

    // If the wall is horizontal, the node above is to the left of the wall
    if (tile.y % 3 === 0) {
      return [node, Direction.Right];
    }

    // If the wall is vertical, the node above is above the wall
    if (tile.x % 3 === 0) {
      return [node, Direction.Down];
    }

    // This shouldn't happen so let's be loud about it
    throw new Error('Somehow we failed to find the wall.');
  }

  /** Remove a wall if it exists, or create it if it does not exist. */
  toggleWall(node: MazeNode, direction: Direction) {
    // Can't toggle edge walls
    if (node.isEdgeWall(direction) || node === this.nullNode) {
      return false;
    }

    const otherNode = this.getNode(...gridMove(node.x, node.y, direction, 1));

    const newLinkValue = !node.links[direction];
    node.setLink(direction, newLinkValue);
    otherNode.setLink(oppositeDirection(direction), newLinkValue);
    this.syncTilemapForNode(node);
    this.syncTilemapForNode(otherNode);

    return true;
  }

  /** Remove a wall permanently from the maze. Edge walls cannot be removed. Returns whether a wall was successfully removed. */
  removeWall(node: MazeNode, direction: Direction) {
    // Node's gotta exist
    if (!node || node === this.nullNode) {
      return false;
    }

    // Can't remove edge walls
    if (node.isEdgeWall(direction)) {
      return false;
    }

    const otherNode = this.getNode(...gridMove(node.x, node.y, direction, 1));

    node.setLink(direction, false);
    otherNode.setLink(oppositeDirection(direction), false);
    this.syncTilemapForNode(node);
    this.syncTilemapForNode(otherNode);

    this.scene.events.emit('removewall', node, direction);

    return true;
  }

  syncTilemapForNode(node: MazeNode) {
    if (node.isEdge) {
      return;
    }

    const tileX = node.x * 3;
    const tileY = node.y * 3;

    // Draw central node
    let nodeIndex = 16;
    if (node.links.up) {
      nodeIndex += 1;
    }
    if (node.links.right) {
      nodeIndex += 2;
    }
    if (node.links.down) {
      nodeIndex += 4;
    }
    if (node.links.left) {
      nodeIndex += 8;
    }
    this.putTileAt(nodeIndex, tileX, tileY);

    // Draw or clear walls
    for (const direction of DIRECTIONS) {
      // Skip edge walls as they're invisible
      if (node.isEdgeWall(direction)) {
        continue;
      }

      const wallIndex = [Direction.Up, Direction.Down].includes(direction) ? 1 : 2;
      if (node.links[direction] === true) {
        this.putTileAt(wallIndex, ...gridMove(tileX, tileY, direction, 1));
        this.putTileAt(wallIndex, ...gridMove(tileX, tileY, direction, 2));
      } else if (node.links[direction] === false) {
        this.putTileAt(-1, ...gridMove(tileX, tileY, direction, 1));
        this.putTileAt(-1, ...gridMove(tileX, tileY, direction, 2));
      }
    }
  }

  syncTilemap() {
    // Add empty tiles along border to block player from walking into walls
    for (let x = 0; x < this.tilemap.width; x++) {
      this.putTileAt(0, x, 0);
      this.putTileAt(0, x, this.tilemap.height - 1);
    }
    for (let y = 0; y < this.tilemap.height; y++) {
      this.putTileAt(0, 0, y);
      this.putTileAt(0, this.tilemap.width - 1, y);
    }

    // Open walls at doorways
    for (const direction of DIRECTIONS) {
      if (this.doors[direction] !== null) {
        switch (direction) {
          case Direction.Up:
            this.putTileAt(-1, this.tilemap.width / 2, 0);
            this.putTileAt(-1, this.tilemap.width / 2 - 1, 0);
            break;
          case Direction.Right:
            this.putTileAt(-1, this.tilemap.width - 1, this.tilemap.height / 2);
            this.putTileAt(-1, this.tilemap.width - 1, this.tilemap.height / 2 - 1);
            break;
          case Direction.Down:
            this.putTileAt(-1, this.tilemap.width / 2, this.tilemap.height - 1);
            this.putTileAt(-1, this.tilemap.width / 2 - 1, this.tilemap.height - 1);
            break;
          case Direction.Left:
            this.putTileAt(-1, 0, this.tilemap.height / 2);
            this.putTileAt(-1, 0, this.tilemap.height / 2 - 1);
            break;
        }
      }
    }

    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        this.syncTilemapForNode(this.getNode(x, y));
      }
    }
  }

  deserializeLinks(serializedLinks: string) {
    const wallArray = Uint8Array.fromBase64(serializedLinks);
    for (let k = 0; k < this.nodes.length; k++) {
      const node = this.nodes[k];
      node.setLinks(
        this.deserializeLink(wallArray.at(k * 4)!),
        this.deserializeLink(wallArray.at(k * 4 + 1)!),
        this.deserializeLink(wallArray.at(k * 4 + 2)!),
        this.deserializeLink(wallArray.at(k * 4 + 3)!),
      );
    }
  }

  serializeLinks() {
    // Could be smarter but I just want a compact form for the JS file that will hold rooms
    // Each node is stored as 4 ints, one for each link (top, right, bottom, left)
    // 0 == null, 1 == false, 2 == true
    const wallArray = new Uint8Array(this.nodes.length * 4);
    for (let k = 0; k < this.nodes.length; k++) {
      const node = this.nodes[k];
      wallArray.set(
        [
          this.serializeLink(node.links.up),
          this.serializeLink(node.links.right),
          this.serializeLink(node.links.down),
          this.serializeLink(node.links.left),
        ],
        k * 4,
      );
    }

    return wallArray.toBase64();
  }

  serializeLink(linkValue: boolean | null) {
    if (linkValue === null) {
      return 0;
    }

    return linkValue ? 2 : 1;
  }

  deserializeLink(serializedLink: number) {
    if (serializedLink === 0) {
      return null;
    }

    return serializedLink === 1 ? false : true;
  }

  clear() {
    const { width, height } = this;

    for (const node of this.nodes) {
      if (!node.isEdge) {
        node.setLinks(false, false, false, false);
      }
    }

    // Fill in walls and block off exits of corners and edges
    this.getNode(0, 0).setLinks(null, true, true, null);
    this.getNode(0, height - 1).setLinks(true, true, null, null);
    this.getNode(width - 1, 0).setLinks(null, null, true, true);
    this.getNode(width - 1, height - 1).setLinks(true, null, null, true);
    for (let x = 1; x < width - 1; x++) {
      this.getNode(x, 0).setLinks(null, true, false, true);
      this.getNode(x, height - 1).setLinks(false, true, null, true);
    }
    for (let y = 1; y < height - 1; y++) {
      this.getNode(0, y).setLinks(true, false, true, null);
      this.getNode(width - 1, y).setLinks(true, null, true, false);
    }
  }

  randomize() {
    const edges = this.edges;

    const activeBranches: [MazeNode, number][] = [];
    const connectedNodes = new Set(edges);

    const extendBranch = (direction: Direction, node: MazeNode, weight = 1) => {
      const adjacentNode = this.getNode(...gridMove(node.x, node.y, direction));
      node.links[direction] = true;
      adjacentNode.links[oppositeDirection(direction)] = true;
      activeBranches.push([adjacentNode, weight]);
      connectedNodes.add(adjacentNode);
    };

    // Pick random edges as roots, skipping corners
    for (const node of edges.filter((e) => !e.isCorner)) {
      if (Math.random() > Maze.EDGE_BRANCH_THRESHOLD) {
        // Only one direction should be open for edges
        const direction = node.openDirections[0];

        // Edge case: Prevent corners from being closed off by checking if adjacent node is already connected
        const adjacentNode = this.getNode(...gridMove(node.x, node.y, direction));
        if (connectedNodes.has(adjacentNode)) {
          continue;
        }

        // Add a wall and queue the adjacent node for tree building
        extendBranch(direction, node);
      }
    }

    // Failsafe: If no roots were generated, force at least one
    if (activeBranches.length === 0) {
      const node = this.getNode(0, Math.floor(randomRange(1, this.height - 1)));
      extendBranch(Direction.Right, node);
    }

    // Randomly grow branches outward until there are no unconnected nodes
    while (activeBranches.length > 0) {
      const index = weightedIndex(activeBranches);
      const [node, weight] = activeBranches.splice(index, 1)[0];
      const prevWallDirection = DIRECTIONS.find((d) => node.links[d] === true)!;
      const prevWallOrientation = getOrientation(prevWallDirection);

      for (const direction of DIRECTIONS) {
        if (node.links[direction] !== false) {
          continue;
        }

        const adjacentNode = this.getNode(...gridMove(node.x, node.y, direction));
        if (connectedNodes.has(adjacentNode)) {
          continue;
        }

        // Prefer longer branches and turns
        let extendedWeight = weight;
        if (extendedWeight < Maze.LONGER_WALL_CUTOFF) {
          extendedWeight *= Maze.LONGER_WALL_MULT;
        }
        if (getOrientation(direction) !== prevWallOrientation) {
          extendedWeight *= Maze.WALL_TURN_MULT;
        } else {
          extendedWeight *= Maze.WALL_STRAIGHT_MULT;
        }
        extendBranch(direction, node, extendedWeight);
      }
    }
  }

  static EDGE_BRANCH_THRESHOLD = 0.75;
  static LONGER_WALL_MULT = 1.4;
  static LONGER_WALL_CUTOFF = 100;
  static WALL_TURN_MULT = 1.2;
  static WALL_STRAIGHT_MULT = 0.8;

  static preload(scene: Phaser.Scene) {
    scene.load.image('maze', 'img/maze.png');
    scene.load.image('collisionTiles', 'img/collision_tiles.png');
  }

  static generate(
    scene: BattleScene,
    x: number,
    y: number,
    width: number,
    height: number,
    doors: DoorsConfig,
    serializedLinks?: string,
  ) {
    const maze = new Maze(scene, x, y, width, height, doors);

    // Skip generation in debug mode
    if (window.location.search.includes('debug')) {
      return maze;
    }

    if (serializedLinks !== undefined) {
      maze.deserializeLinks(serializedLinks);
      return maze;
    }

    maze.clear();
    maze.randomize();

    return maze;
  }
}
